import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { AuthContext } from '../context/AuthContext';
import { getLogs } from '../utils/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { ROLE_SUPERADMIN } from '../utils/roles';

const LogsPage = () => {
	const { user } = useContext(AuthContext);
	const [logs, setLogs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [sortField, setSortField] = useState('created_at');
	const [sortDirection, setSortDirection] = useState('desc');

	useEffect(() => {
		fetchLogs();
	}, []);

	const fetchLogs = async () => {
		try {
			const response = await getLogs();
			if (!response.ok) {
				throw new Error('Failed to fetch logs');
			}
			const data = await response.json();
			setLogs(data.data || []);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const formatDate = (dateString) => {
		const date = new Date(dateString);
		return date.toLocaleString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit'
		});
	};

	const handleSort = (field) => {
		if (sortField === field) {
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
	};

	const getSortIcon = (field) => {
		if (sortField !== field) return null;
		return sortDirection === 'asc' ? faArrowUp : faArrowDown;
	};

	const sortedLogs = useMemo(() => {
		const sorted = [...logs].sort((a, b) => {
			let aValue, bValue;

			switch (sortField) {
				case 'device_id':
					aValue = a.device_id || '';
					bValue = b.device_id || '';
					break;
				case 'created_at':
					aValue = new Date(a.created_at).getTime();
					bValue = new Date(b.created_at).getTime();
					break;
				case 'log':
					aValue = a.log || '';
					bValue = b.log || '';
					break;
				default:
					return 0;
			}

			if (typeof aValue === 'string') {
				const comparison = aValue.localeCompare(bValue);
				return sortDirection === 'asc' ? comparison : -comparison;
			}

			return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
		});

		return sorted;
	}, [logs, sortField, sortDirection]);

	if (user?.roleId !== ROLE_SUPERADMIN) {
		return <Navigate to="/" replace />;
	}

	if (loading) {
		return <div className="page-gradient min-h-screen flex items-center justify-center">Loading...</div>;
	}

	if (error) {
		return <div className="page-gradient min-h-screen flex items-center justify-center text-status-critical">{error}</div>;
	}

	return (
		<div className="page-gradient min-h-screen">
			<Header />

			<main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
				<section className="mb-8">
					<h1 className="text-3xl font-bold text-text-strong mb-2">Device Logs</h1>
					<p className="text-text-weak">View all device logs from the system</p>
				</section>

				<div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-card overflow-hidden">
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th
									className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
									onClick={() => handleSort('device_id')}
									data-testid="sort-device-id"
								>
									<div className="flex items-center space-x-1">
										<span>Device ID</span>
										{sortField === 'device_id' && (
											<FontAwesomeIcon icon={getSortIcon('device_id')} className="text-gray-400" />
										)}
									</div>
								</th>
								<th
									className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
									onClick={() => handleSort('created_at')}
									data-testid="sort-timestamp"
								>
									<div className="flex items-center space-x-1">
										<span>Timestamp</span>
										{sortField === 'created_at' && (
											<FontAwesomeIcon icon={getSortIcon('created_at')} className="text-gray-400" />
										)}
									</div>
								</th>
								<th
									className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
									onClick={() => handleSort('log')}
									data-testid="sort-log"
								>
									<div className="flex items-center space-x-1">
										<span>Log Message</span>
										{sortField === 'log' && (
											<FontAwesomeIcon icon={getSortIcon('log')} className="text-gray-400" />
										)}
									</div>
								</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{sortedLogs.map(log => (
								<tr key={log.id} data-testid="log-row" data-device-id={log.device_id}>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
										{log.device_id}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
										{formatDate(log.created_at)}
									</td>
									<td className="px-6 py-4 text-sm text-gray-900">
										{log.log}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{logs.length === 0 && (
					<div className="text-center py-12">
						<p data-testid="logs-empty" className="text-lg text-gray-600">No logs found.</p>
					</div>
				)}
			</main>

			<Footer />
		</div>
	);
};

export default LogsPage;
